import { DocumentSnapshot, onSnapshot } from 'firebase/firestore'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import useHandleError from '../../../../hooks/useHandleError'
import FirestoreUser from '../../../../types/FirestoreUser'
import userDoc from '../../../../utils/userDoc'
import { primaryServices } from '../../../../utils/firebaseServiceContext'

interface UseUserDataReturn {
  snap: DocumentSnapshot<FirestoreUser> | undefined
  user: FirestoreUser | undefined
  profile: FirestoreUser['profile'] | undefined
  ref: ReturnType<typeof userDoc>
  uid: string
  triggerRefresh: () => void
}

export function useUserData(): UseUserDataReturn {
  const [snap, setSnap] = useState<DocumentSnapshot<FirestoreUser>>()
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const params = useParams<{ id: string }>()
  const handleError = useHandleError()

  const uid = useMemo(() => {
    return params.id || 'impossible'
  }, [params])

  const ref = useMemo(() => {
    return userDoc(uid, primaryServices.firestore)
  }, [uid])

  useEffect(() => {
    const unsubscribe = onSnapshot(ref, setSnap, handleError)
    return () => unsubscribe()
  }, [ref, handleError, refreshTrigger])

  const user = snap?.data()
  const profile = user?.profile

  function triggerRefresh() {
    setRefreshTrigger((prev) => prev + 1)
  }

  return {
    snap,
    user,
    profile,
    ref,
    uid,
    triggerRefresh,
  }
}
